import { Button, Divider } from '@mui/material';
import { INCOME_OR_EXPENSE_LIST } from 'adminConstants/routes';
import AllocationsManager, {
  Allocation,
} from 'components/new/AllocationsManager';
import FullHeightView from 'components/new/FullHeightView';
import { useLayoutContext } from 'contexts/LayoutContext';
import { AllocationRule } from 'further-types/lp/allocations';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { FundingSource } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import { compact, round } from 'lodash';
import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { FormValues } from '../FormWrapper';
import InputDetailsForm from './InputDetailsForm';

type Props = {
  id?: string;
  onNext: () => void;
};

const StepAddAndAllocate: FC<Props> = ({ id, onNext }) => {
  const history = useHistory();
  const { vehicle } = useLayoutContext();
  const { setValue, watch, handleSubmit, formState } =
    useFormContext<FormValues>();

  const formValues = watch();

  useEffect(() => {
    if (formValues.amountExVatTcy !== null && formValues.vatRate !== null) {
      const amountIncVatTcy = round(
        formValues.amountExVatTcy * (1 + formValues.vatRate / 100),
        2,
      );
      if (formValues.amountIncVatTcy !== amountIncVatTcy) {
        setValue('amountIncVatTcy', amountIncVatTcy);
      }
    }
  }, [formValues.amountExVatTcy, formValues.vatRate]);

  const handleCancel = () => {
    history.push(INCOME_OR_EXPENSE_LIST);
  };

  const handleAllocationRuleChange = (allocationRule: AllocationRule) => {
    setValue('allocationRule', allocationRule);
  };

  const handleAllocationsChange = (allocations: Array<Allocation>) => {
    setValue('allocations', allocations);
  };

  const handleUnlinkAction = () => {
    setValue('sourceAllocationActionId', undefined);
  };

  const allocationsLabel =
    formValues.category === IncomeOrExpenseType.Income ? 'Income' : 'Expense';

  const incomeOrExpenseIds = compact(
    formValues.apportionmentPeriods.map((period) => period.id),
  );

  return (
    <FullHeightView
      actionBar={
        <Row justify="space-between">
          <Button variant="outlined" onClick={handleCancel} size="large">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSubmit(onNext)}
            size="large"
          >
            Proceed to summary
          </Button>
        </Row>
      }
    >
      <form>
        <Column spacing="lg">
          <InputDetailsForm
            id={id}
            vehicleId={formValues.vehicleId}
            incomeOrExpense={formValues.category}
            transactionCurrency={formValues.currencyCode}
          />
          <Divider />
          <AllocationsManager
            vehicleId={formValues.vehicleId}
            allocations={formValues.allocations ?? []}
            allocatedActionLabel={allocationsLabel}
            allocatedAmountTcy={formValues.amountIncVatTcy}
            currency={formValues.currencyCode}
            baseCurrency={vehicle?.baseCurrency ?? CurrencyCode.GBP}
            allocationRule={formValues.allocationRule}
            allocationsSourceActionId={
              formValues.sourceAllocationActionId ?? undefined
            }
            onAllocationRuleChange={handleAllocationRuleChange}
            onAllocationsChange={handleAllocationsChange}
            useGeneralCapital={
              formValues.fundingSource ===
              FundingSource.GeneralCapitalOnAccounts
            }
            error={formState.errors.allocations?.message}
            unlinkAction={handleUnlinkAction}
            currentActionIds={incomeOrExpenseIds}
            date={formValues.startDate}
          />
        </Column>
      </form>
    </FullHeightView>
  );
};

export default StepAddAndAllocate;
